import { computed, ref, type Ref } from "vue"
import type { BaseModel } from "@/models/base.model"
import { useEntitySaveStateStore } from "@/store/model-save-state.store"
import type { StatusCodeHandler } from "@/interfaces/http/status-code-handler.interface"
import { type IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface"

type createOrUpdateFunctionType<T extends BaseModel> = (
  entity: T,
  requestConfig?: IRepositoryRequestConfig
) => Promise<T | undefined>

export default function useEntityUpdater<T extends BaseModel>(
  entity: Ref<T>,
  updateFn: createOrUpdateFunctionType<T>,
  createFn?: createOrUpdateFunctionType<T>,
  statusCodeHandlers: StatusCodeHandler[] = [],
  onUpdateError?: (error: unknown) => void,
  queueUpdates = true
) {
  /* Keep model save state in a centralized store. This serializes updates from separate entity updaters. */
  const modelSaveStore = useEntitySaveStateStore()

  let entityIsBeingCreated = false
  let objectCloneWaitingForUpdate: T | undefined
  let lastUpdateNumber = 0
  let disabled = false

  const isLastUpdateFinished = ref(true)
  const errorEncountered = ref(false)

  // True if creating or updating the entity.
  const isBusy = computed(() => {
    return isCreating.value || !isLastUpdateFinished.value
  })

  const isCreating = computed(() => {
    return modelSaveStore.isCreating(entity.value)
  })

  async function update(presentEntity?: T) {
    if (presentEntity) {
      // Allow passing the present or 'actual' entity for update. This is useful when a debounced update is used, and
      // the entity is being modified (e.g. by network request completing) while the update is still scheduled.
      // This would cause this function to pass the system-modified entity to the update function.
      // Passing presentEntity ensures that the entity is updated with the latest (user) data.

      // Remove isSaved property from the entity, as it should be managed independently.
      const { isSaved, ...filteredEntity } = presentEntity // eslint-disable-line @typescript-eslint/no-unused-vars
      Object.assign(entity.value, filteredEntity)
    }

    errorEncountered.value = false

    if (isCreating.value) {
      if (entityIsBeingCreated) {
        // If the entity is being created, we need to wait for the creation to finish before updating.
        // We store the original object, so we can update it after creation.
        objectCloneWaitingForUpdate = entity.value.copy()
      }
      return
    }

    if (entity.value.isSaved) {
      return await tryUpdate(updateFn, entity.value.copy())
    } else {
      const instance = await tryCreate(entity.value)

      if (!instance) {
        // If the entity was not created, there is nothing to update.
        objectCloneWaitingForUpdate = undefined
      }

      if (objectCloneWaitingForUpdate) {
        // If the entity was modified while it was being created, we need to update it now.
        objectCloneWaitingForUpdate.id = instance?.id

        // Immediately restore the user's modifications to the reactive entity to prevent
        // the UI from briefly showing stale server data (e.g. empty quantity in rental mutation processing rows).
        const { isSaved, ...filteredClone } = objectCloneWaitingForUpdate // eslint-disable-line @typescript-eslint/no-unused-vars
        Object.assign(entity.value, filteredClone)

        return await tryUpdate(updateFn, objectCloneWaitingForUpdate)
      }
      return instance
    }
  }

  async function tryCreate(entity: T) {
    if (!createFn) {
      throw new Error(
        "useEntityUpdater() creation procedure invoked, but no create function was provided"
      )
    }
    if (disabled) {
      return
    }

    modelSaveStore.setCreationState(entity, true)
    entityIsBeingCreated = true

    let instance

    try {
      instance = await createFn(entity, { statusCodeHandlers })
    } catch (e) {
      errorEncountered.value = true
      console.error("Error occurred while creating the entity.", e)
    } finally {
      modelSaveStore.setCreationState(entity, false)
      entityIsBeingCreated = false
    }
    return instance
  }

  async function tryUpdate(updateFn: createOrUpdateFunctionType<T>, entity: T) {
    if (disabled) {
      return
    }

    let result

    const updateNumber = ++lastUpdateNumber
    isLastUpdateFinished.value = false
    try {
      const executeUpdate = async () => {
        return await updateFn(entity, {
          // In case of error, onFinishUpdate must be called before the statusCodeHandler(s).
          beforeErrorHandlingCallback: () => onFinishUpdate(updateNumber),
          statusCodeHandlers
        })
      }
      result = queueUpdates
        ? await modelSaveStore.enqueueLatestUpdate(entity, executeUpdate)
        : await executeUpdate()
      onFinishUpdate(updateNumber)
    } catch (e: unknown) {
      if (onUpdateError) {
        onUpdateError(e)
      }
      errorEncountered.value = true

      /*
              Sometimes an entity is deleted while a PUT request is being sent. Possible causes:
              - update() is unjustly called because of some synchronisation issue (main cause).
              - User edits an entity while another user deletes it (very unlikely because of locking).
              */
      console.log(
        "an error occured in entity-updater.ts. if it's a 404, it's probably nothing to worry about"
      )
    }
    return result
  }

  function onFinishUpdate(updateNumber: number) {
    if (updateNumber === lastUpdateNumber) {
      isLastUpdateFinished.value = true
    }
  }

  function disable() {
    disabled = true
  }

  return {
    isCreating,
    isBusy,
    isLastUpdateFinished,
    errorEncountered,
    update,
    disable
  }
}
